import {
  apply,
  compose,
  position,
  replace,
  transform,
  validate,
  type Operation,
} from './textOperation';
export interface Canonical {
  version: number;
  operationId: string;
  operation: Operation;
}
interface Pending {
  id: string;
  operation: Operation;
  sent?: { version: number; operation: Operation };
}
export class LiveDocument {
  shadow: string;
  body: string;
  version: number;
  pending: Pending[] = [];
  constructor(body: string, version: number) {
    this.shadow = body;
    this.body = body;
    this.version = version;
  }
  edit(next: string) {
    const op = replace(this.body, next);
    if (op.every((p) => typeof p === 'number')) return;
    const last = this.pending.at(-1);
    if (last && !last.sent) last.operation = compose(last.operation, op);
    else this.pending.push({ id: crypto.randomUUID(), operation: op });
    this.body = next;
  }
  request() {
    const head = this.pending[0];
    if (!head) return null;
    head.sent ??= { version: this.version, operation: head.operation };
    return { version: head.sent.version, operationId: head.id, operation: head.sent.operation };
  }
  receive(edit: Canonical): Operation | null {
    if (edit.version <= this.version) return null;
    if (edit.version !== this.version + 1) throw Error('Missing canonical edits');
    const operation = validate(edit.operation, 5000),
      shadow = apply(operation, this.shadow);
    let remote: Operation | null = null;
    if (this.pending[0]?.id === edit.operationId) {
      if (apply(this.pending[0].operation, this.shadow) !== shadow)
        throw Error('Acknowledgment mismatch');
      this.pending.shift();
    } else {
      remote = operation;
      const next: Operation[] = [];
      for (const p of this.pending) {
        const pair = transform(p.operation, remote);
        next.push(pair[0]);
        remote = pair[1];
      }
      this.body = apply(remote, this.body);
      this.pending.forEach((p, i) => {
        p.operation = next[i]!;
      });
    }
    this.shadow = shadow;
    this.version = edit.version;
    if (this.pending.reduce((text, p) => apply(p.operation, text), this.shadow) !== this.body)
      throw Error('Draft did not converge');
    return remote;
  }
  rebaseRejected(id: string) {
    if (this.pending[0]?.id === id) delete this.pending[0].sent;
  }
  resolveRejectedText(id: string) {
    if (this.pending[0]?.id !== id) return;
    const op = this.pending
      .slice(1)
      .reduce((a, p) => compose(a, p.operation), this.pending[0].operation);
    this.pending = [{ id: crypto.randomUUID(), operation: op }];
  }
  localPosition(pos: number) {
    return this.pending.reduce((n, p) => position(p.operation, n), pos);
  }
  serialize() {
    return JSON.stringify({ shadow: this.shadow, version: this.version, pending: this.pending });
  }
  static restore(raw: string) {
    if (raw.length > 262144) throw Error('Recovery too large');
    const r = JSON.parse(raw) as { shadow: string; version: number; pending: Pending[] };
    if (
      typeof r.shadow !== 'string' ||
      Array.from(r.shadow).length > 5000 ||
      !Number.isInteger(r.version) ||
      r.version < 0 ||
      !Array.isArray(r.pending) ||
      r.pending.length > 1000
    )
      throw Error('Invalid recovery');
    const doc = new LiveDocument(r.shadow, r.version),
      ids = new Set<string>();
    for (const [i, p] of r.pending.entries()) {
      if (typeof p.id !== 'string' || ids.has(p.id)) throw Error('Invalid pending identity');
      ids.add(p.id);
      const operation = validate(p.operation);
      let sent: Pending['sent'];
      if (p.sent) {
        if (
          i !== 0 ||
          !Number.isInteger(p.sent.version) ||
          p.sent.version < 0 ||
          p.sent.version > doc.version
        )
          throw Error('Invalid pending version');
        sent = { version: p.sent.version, operation: validate(p.sent.operation, 5000) };
      }
      doc.body = apply(operation, doc.body);
      doc.pending.push({ id: p.id, operation, ...(sent ? { sent } : {}) });
    }
    return doc;
  }
}
